import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { EmployeeLayout } from '../../components/EmployeeLayout';
import { employeeAPI, authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { User, Phone, Lock, Loader2, Eye, EyeOff, Mail } from 'lucide-react';
import { YTDSummaryCard } from '../../components/canadian/YTDSummaryCard';
import { VacationBalanceCard } from '../../components/canadian/VacationBalanceCard';
import { useCanadianPayroll } from '../../context/CanadianPayrollContext';

export default function EmployeeProfile() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pw, setPw] = useState({ current: '', new: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    employeeAPI
      .getProfile()
      .then(res => setProfile({ name: res.data.name || '', phone: res.data.phone || '' }))
      .catch(console.error);
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setLoading(true);
    try {
      const res = await employeeAPI.updateProfile({
        name: profile.name,
        phone: profile.phone,
      });
      updateUser({ ...user, name: res.data.name, phone: res.data.phone });
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pw.new !== pw.confirm) { toast.error('New passwords do not match'); return; }
    if (pw.new.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pw.new === pw.current) { toast.error('New password must differ from current password'); return; }
    setPwLoading(true);
    try {
      await authAPI.changePassword({
        current_password: pw.current,
        new_password: pw.new,
        confirm_password: pw.confirm,
      });
      const { data } = await authAPI.refreshToken();
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      updateUser(data.user);
      setPw({ current: '', new: '', confirm: '' });
      toast.success('Password changed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const togglePw = (key) => setShowPw(p => ({ ...p, [key]: !p[key] }));
  const { caEnabled } = useCanadianPayroll();

  return (
    <EmployeeLayout>
      <div className="p-6 max-w-2xl space-y-6" data-testid="employee-profile">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          My Profile
        </h1>

        {/* Profile Info */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
            <User size={16} className="text-slate-500" />
            Personal Information
          </h2>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="emp-name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="emp-name"
                  data-testid="profile-name-input"
                  className="pl-10"
                  value={profile.name}
                  onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="emp-phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="emp-phone"
                  data-testid="profile-phone-input"
                  className="pl-10"
                  value={profile.phone}
                  onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="emp-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="emp-email"
                  className="pl-10 bg-slate-50 text-slate-400"
                  value={user?.email || ''}
                  disabled
                />
              </div>
              <p className="text-xs text-slate-400">Contact your manager to change your email address.</p>
            </div>

            <Button
              type="submit"
              data-testid="profile-save-btn"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
            <Lock size={16} className="text-slate-500" />
            Change Password
          </h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {[
              { id: 'pw-current', label: 'Current Password', key: 'current', placeholder: 'Enter current password' },
              { id: 'pw-new', label: 'New Password', key: 'new', placeholder: 'At least 8 characters' },
              { id: 'pw-confirm', label: 'Confirm New Password', key: 'confirm', placeholder: 'Repeat new password' },
            ].map(({ id, label, key, placeholder }) => (
              <div key={id} className="space-y-1.5">
                <Label htmlFor={id}>{label}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id={id}
                    data-testid={`pw-${key}-input`}
                    type={showPw[key] ? 'text' : 'password'}
                    placeholder={placeholder}
                    className="pl-10 pr-10"
                    value={pw[key]}
                    onChange={e => setPw(p => ({ ...p, [key]: e.target.value }))}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePw(key)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPw[key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            ))}
            <Button
              type="submit"
              data-testid="pw-change-btn"
              disabled={pwLoading}
              variant="outline"
              className="border-slate-300"
            >
              {pwLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>
        </div>

        {/* Canadian Payroll — self-service view (employee sees their own YTD + vacation) */}
        {caEnabled && user?.id && (
          <>
            <YTDSummaryCard employeeId={user.id} />
            <VacationBalanceCard employeeId={user.id} canEdit={false} />
          </>
        )}
      </div>
    </EmployeeLayout>
  );
}
